// The module Dashboard's blocks (BP-15, View 1100), shared: the hero, a KPI tile that
// opens the list filtered to what it counts (BP-93), a list of records each opening its
// record, and a by-group breakdown table. They draw the `.an-home` vocabulary of
// aeronet-home.css and must sit inside <DashboardPage>. A module supplies the figures,
// labels and link targets; the look is owned here and in the CSS.
//
// Promoted 2026-10-21 from the Due Date Control dashboard (DueDate/DueDateDashboard.tsx,
// their first consumer) when the Action Register dashboard needed the same blocks.
import React from "react";
import { Link } from "react-router-dom";

const DASH = "—";

const days = (n: number) => `${n.toLocaleString()} day${Math.abs(n) === 1 ? "" : "s"}`;

/** Today, long form, for the hero's date line. */
export function formatDashboardToday(): string {
  return new Date().toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export interface DashboardHeroProps {
  /** The hero figure: a server COUNT, never a page length. */
  value: number;
  /** After the date: what the figure counts, e.g. "open due dates, of 40 assigned". */
  caption: React.ReactNode;
  onRefresh: () => void;
  /** False while a read is in flight (disables refresh). */
  loaded: boolean;
  /** The hero's button to the full list. */
  actionLabel: string;
  onAction: () => void;
  "data-testid"?: string;
}

/** Block 1: the hero figure, today's date, Live snapshot, refresh and the list button. */
export const DashboardHero: React.FC<DashboardHeroProps> = ({
  value,
  caption,
  onRefresh,
  loaded,
  actionLabel,
  onAction,
  "data-testid": testId = "hero-open",
}) => (
  <div className="hero">
    <div>
      <div className="hero__value tnum" data-testid={testId}>
        {value.toLocaleString()}
      </div>
      <div className="hero__date">
        {formatDashboardToday()}
        <span className="hero__date-sub">
          {" · "}
          {caption}
        </span>
      </div>
    </div>
    <div className="hero__actions">
      <span className="tag-sync">
        <span className="tag-sync__dot" />
        Live snapshot
      </span>
      <button
        type="button"
        className="btn btn--icon"
        onClick={onRefresh}
        title="Refresh dashboard"
        aria-label="Refresh dashboard"
        disabled={!loaded}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12a9 9 0 1 1-2.6-6.3" />
          <path d="M21 3v6h-6" />
        </svg>
      </button>
      <button type="button" className="btn" onClick={onAction}>
        {actionLabel}
      </button>
    </div>
  </div>
);

export interface KpiTileProps {
  id: string;
  label: string;
  /** `null` = not established: the tile shows "—" and is not a link (BP-15 refinement). */
  value: number | null;
  sub: string;
  /** The list filtered to exactly what the tile counts (BP-93); omit for a plain tile. */
  to?: string;
  tone?: "neg" | "warn";
}

/** One KPI tile. With `to` it is a router Link (relative="path", the mode layout is pathless). */
export const KpiTile: React.FC<KpiTileProps> = ({ id, label, value, sub, to, tone }) => {
  if (value == null) {
    return (
      <div className="kpi kpi--unavailable" data-testid={`kpi-${id}`}>
        <div className="kpi__label">{label}</div>
        <div className="kpi__value kpi__value--unavailable tnum">{DASH}</div>
        <div className="kpi__meta">
          <span className="kpi__sub">{sub}</span>
        </div>
      </div>
    );
  }
  const body = (
    <>
      <div className="kpi__label">{label}</div>
      <div className="kpi__value tnum">{value.toLocaleString()}</div>
      <div className="kpi__meta">
        <span className="kpi__sub">{sub}</span>
      </div>
    </>
  );
  const className = `kpi${to ? " kpi--link" : ""}${tone ? ` kpi--${tone}` : ""}`;
  return to ? (
    <Link
      className={className}
      to={to}
      relative="path"
      data-testid={`kpi-${id}`}
      aria-label={`${label}: ${value.toLocaleString()} — open the list of them`}
    >
      {body}
    </Link>
  ) : (
    <div className={className} data-testid={`kpi-${id}`}>
      {body}
    </div>
  );
};

/** How far a row is from its due date, as a chip in the house tones. */
export function DaysChip({ value }: { value: number | null | undefined }) {
  if (value == null) return null;
  if (value < 0) return <span className="chip chip--neg">{days(-value)} overdue</span>;
  if (value === 0) return <span className="chip chip--warn">Due today</span>;
  return <span className={`chip ${value <= 7 ? "chip--warn" : "chip--gray"}`}>in {days(value)}</span>;
}

export interface RecordRow {
  id: number;
  /** The chip before the title; defaults to `#<id>`. */
  chip?: string;
  title: string;
  sub: string;
  /** Already formatted in the tenant's date format (BP-83). */
  date: string;
  /** Days to the due date; negative = overdue. */
  days: number | null;
}

interface RecordRowListProps {
  rows: RecordRow[];
  empty: string;
  open: (id: number) => void;
  testId: string;
}

/** A card's list of records — chip, title over sub-line, date, days — each opening its record. */
export const RecordRowList: React.FC<RecordRowListProps> = ({ rows, empty, open, testId }) => (
  <div className="list" data-testid={testId}>
    {rows.length === 0 ? (
      <div className="empty">{empty}</div>
    ) : (
      rows.map((r) => (
        <div
          key={r.id}
          className="list__row"
          role="button"
          tabIndex={0}
          onClick={() => open(r.id)}
          onKeyDown={(e) => {
            if (e.key === "Enter") open(r.id);
          }}
        >
          <span className="id-chip">{r.chip ?? `#${r.id}`}</span>
          <span className="list__name">
            {r.title}
            <span className="list__sub">{r.sub}</span>
          </span>
          <span className="ago-chip">{r.date || DASH}</span>
          <DaysChip value={r.days} />
        </div>
      ))
    )}
  </div>
);

export interface BreakdownRow {
  key: string | number;
  name: string;
  open: number;
  overdue: number;
  due_30: number;
}

interface BreakdownTableProps {
  rows: BreakdownRow[];
  nameHeader: string;
  empty: string;
  testId: string;
}

/** By group (section, assignee, work stream): open / overdue / due ≤30d, the server's counts. */
export const BreakdownTable: React.FC<BreakdownTableProps> = ({ rows, nameHeader, empty, testId }) =>
  rows.length === 0 ? (
    <div className="empty">{empty}</div>
  ) : (
    <div className="dash-table__scroll">
      <table className="dash-table" data-testid={testId}>
        <thead>
          <tr>
            <th>{nameHeader}</th>
            <th>Open</th>
            <th>Overdue</th>
            <th>Due ≤30d</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td className="cell--strong">{r.name}</td>
              <td className="cell--num">{r.open.toLocaleString()}</td>
              <td className="cell--num">{r.overdue.toLocaleString()}</td>
              <td className="cell--num">{r.due_30.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
